// Browser-side image upload helper. Two-step flow:
//   1. Ask the API for a presigned PUT URL + the headers the browser
//      must echo (the signature includes them).
//   2. PUT the file bytes directly to DO Spaces with those headers.
//
// Returns the public CDN URL the caller should store on the proptree
// doc. Throws on any non-2xx so callers can surface errors inline.
import { client } from '../main.js';

export type ImageUploadKind =
  | 'proptree-profile'
  | 'proptree-button'
  | 'agent-avatar'
  | 'user-avatar'
  | 'agency-logo'
  | 'agency-favicon'
  | 'brand-font'
  | 'brand-audio';

const FONT_TYPES = [
  'font/woff2',
  'font/woff',
  'font/ttf',
  'font/otf',
] as const;
const RASTER_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
const AUDIO_TYPES = ['audio/mpeg', 'audio/mp4', 'audio/wav'] as const;
type UploadContentType =
  | (typeof RASTER_TYPES)[number]
  | (typeof FONT_TYPES)[number]
  | (typeof AUDIO_TYPES)[number]
  | 'image/svg+xml';

/** SVG is accepted only where the asset is genuinely vector artwork. */
function svgAllowedFor(kind: ImageUploadKind): boolean {
  return kind === 'agency-logo' || kind === 'agency-favicon';
}

function isAllowedType(
  type: string,
  kind: ImageUploadKind,
): type is UploadContentType {
  // Font and audio uploads are their own kinds; images never mix with
  // them, and the API refuses the crossover too.
  if (kind === 'brand-font') {
    return (FONT_TYPES as readonly string[]).includes(type);
  }
  if (kind === 'brand-audio') {
    return (AUDIO_TYPES as readonly string[]).includes(type);
  }
  if ((RASTER_TYPES as readonly string[]).includes(type)) return true;
  return type === 'image/svg+xml' && svgAllowedFor(kind);
}

export async function uploadImage({
  file,
  kind,
  proptreeId,
}: {
  file: File;
  kind: ImageUploadKind;
  // Required for `proptree-*` kinds only — those end up referenced on a
  // proptree doc. Account-level kinds (user-avatar, agency-logo,
  // agency-favicon) and `agent-avatar` have no proptree to attach to.
  proptreeId?: string;
}): Promise<string> {
  const contentType = file.type;
  // SVG is vector artwork for brand marks only — the API enforces the
  // same rule; this fails faster and with a clearer message. The type
  // predicate is what narrows `file.type` (a plain string) down to the
  // union the signature endpoint accepts.
  if (!isAllowedType(contentType, kind)) {
    if (kind === 'brand-font') {
      throw new Error('Unsupported font file. Use WOFF2, WOFF, TTF, or OTF.');
    }
    throw new Error(
      svgAllowedFor(kind)
        ? 'Unsupported image type. Use SVG, PNG, JPEG, or WebP.'
        : 'Unsupported image type. Use PNG, JPEG, or WebP.',
    );
  }
  const sig = await client.uploads.getSignature({
    kind,
    ...(proptreeId ? { proptreeId } : {}),
    contentType,
    filename: file.name,
  });
  const res = await fetch(sig.uploadUrl, {
    method: 'PUT',
    headers: sig.requiredHeaders,
    body: file,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Upload failed (${res.status}): ${body.slice(0, 200) || 'no body'}`,
    );
  }
  return sig.publicUrl;
}
